import type {ChangeEvent, ReactElement} from 'react';
import React, {useEffect, useMemo, useRef, useState} from 'react';
import {CommonCaptionCheckbox} from './components/common-caption-checkbox/common-caption-checkbox';
import {HighlightedCaptionTextarea} from './components/highlighted-caption-textarea/highlighted-caption-textarea';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {SocialPlatform, SocialPostCaptionOverwriteEvent} from '@Components/social-media/social-media.types';
import type {SocialAccount} from '@Components/social-media/account.vo';
import useOutsideClick from '@Hooks/useOutsideClick';
import {useAllPublishingParamsForAccount, useCommonCaption, useHasCommonCaption} from '@Components/social-media/social-media.hooks';
import {updateCommonCaption, updatePublishingParamAllCaptions, updatePublishingParamCaption} from '@Components/social-media-wizard/social-media-wizard-reducer';
import {Text, TextSize, TextType} from '@Components/text';
import {Button, Size, Type} from '@Components/button';
import {captionCharacterLimits, CaptionErrorTypes} from '@Components/social-media-wizard/social-media-wizard.types';
import {
  usePostId,
  useAreCaptionsBeingAutoGenerated,
  useIsSocialMediaWizardAiCaptionsEnabled,
  usePlatformSelectionforAccount,
  useWizardAccountsToPublishTo,
  useWizardCaptionErrors,
} from '@Components/social-media-wizard/social-media-wizard.hooks';
import {AiButton} from '@Components/ai-button';
import {IntroPopup} from '@Components/intro-popup';
import {getAssetUrl} from '@Utils/s3.util';
import {GA4EventName} from '@Libraries/ga-events';
import {isUserPremium} from '@Libraries/user.library';
import {AnimatePresence, motion} from 'framer-motion';
import useAutoCaptionGlobal from '@Hooks/auto-captions/useAutoCaptionGlobal';
import {EmojiPickerInput} from '@Components/emoji-picker-input';
import {AIButtonShape, AIButtonVariant} from '@Components/ai-button/ai-button.types';
import styles from './social-post-caption.module.scss';
import {useAppDispatch} from '@/hooks';
import useSelectCaption from '@Hooks/auto-captions/useSelectCaption';
import {CaptionWriterPopoverView} from './components/caption-writer-popover-view/caption-writer-popover-view';
import {getRandomizedLoadingCaption} from '@Components/social-media-wizard/components/social-post-caption/social-post-caption.types';
import {getNonStoryPublishingParams} from '@Libraries/social-media-params.library';
import {useAiCaptionOnboarding} from './social-post-caption.hooks';
import {CaptionTagsPopover} from './components/caption-tags-popover/caption-tags-popover';
import type {CaptionTagsPopoverHandle} from './components/caption-tags-popover/caption-tags-popover.types';

interface SocialPostCaptionProps {
  account: SocialAccount;
}

function SocialPostCaption({account}: SocialPostCaptionProps): ReactElement<SocialPostCaptionProps> | null {
  const [isEmojiSelectorVisible, setIsEmojiSelectorVisible] = useState(false);
  const [isCountErrorVisible, setIsCountErrorVisible] = useState(false);
  const [isHashtagErrorVisible, setIsHashtagErrorVisible] = useState(false);
  const [isCaptionChangeMultiCharacter, setIsCaptionChangeMultiCharacter] = useState(false);
  const [isUserEligibleToSeeTagsPopOver, setIsUserEligibleToSeeTagsPopOver] = useState<boolean>(false);
  const captionTagsPopoverRef = useRef<CaptionTagsPopoverHandle>(null);
  const dispatch = useAppDispatch();
  const allPublishingParamsForAccount = useAllPublishingParamsForAccount(account.id);
  const publishingParamsForAccount = allPublishingParamsForAccount ? getNonStoryPublishingParams(allPublishingParamsForAccount) : null;
  const commonCaption = useCommonCaption();
  const hasCommonCaption = useHasCommonCaption();
  const emojiPickerRef = useRef<HTMLDivElement>(null);
  const iconRef = useRef<HTMLElement>(null);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const textAreaContainerRef = useRef<HTMLDivElement>(null);
  const captionErrors = useWizardCaptionErrors();
  const accountIdsToPublishTo = useWizardAccountsToPublishTo();
  const {closeAutoCaptionMode} = useAutoCaptionGlobal();
  const CAPTION_TEXT_BOX_HEIGHT = 220;
  const CAPTION_CONTAINER_TEXTBOX_HEIGHT = 245;
  const AUTO_CAPTION_BOX_HEIGHT = 280;
  const socialPostCaptionContainerRef = useRef<HTMLDivElement>(null);
  const aiCaptionButtonRef = useRef<HTMLButtonElement>(null);
  const isSocialMediaWizardAiCaptionsEnabled = useIsSocialMediaWizardAiCaptionsEnabled();
  const platformSelection = usePlatformSelectionforAccount(account.id);
  const postId = usePostId();
  const {chooseCaption} = useSelectCaption();
  const areCaptionsBeingAutoGenerated = useAreCaptionsBeingAutoGenerated();
  const [autoGenerationLoadingCaption, setAutoGenerationLoadingCaption] = useState<string>('');
  const [isCaptionWriterOpen, setIsCaptionWriterOpen] = useState<boolean>(false);
  const {
    isAiCaptionButtonPulsating,
    isVisibleAiCaptionReminderPopup,
    shouldShowIntroPopup,
    onAiCaptionClick: onAiCaptionOnboardingClick,
    afterCloseIntroPopup,
    onSeenReminderPopup,
    startPulsatingButtonTimer,
    abortPulsatingButtonTimer,
  } = useAiCaptionOnboarding({
    accountId: account.id,
    accountIdsToPublishTo,
    isUserEligibleToSeeTagsPopOver,
  });

  const getCurrentCaption = (): string => {
    if (hasCommonCaption && commonCaption) {
      return commonCaption;
    }

    if (!publishingParamsForAccount?.caption && textAreaRef.current) {
      return '';
    }
    return publishingParamsForAccount?.caption ?? '';
  };

  const captionCharCount = getCurrentCaption().length;

  useEffect(() => {
    const loadingStateCaption = getRandomizedLoadingCaption();
    setAutoGenerationLoadingCaption(loadingStateCaption);
    const captionOverwriteCallback = (_event: Event): void => {
      triggerTextAreaAnimation();
    };

    window.addEventListener(SocialPostCaptionOverwriteEvent, captionOverwriteCallback);

    return (): void => {
      window.removeEventListener(SocialPostCaptionOverwriteEvent, captionOverwriteCallback);
    };
  }, []);

  useEffect(() => {
    if (captionErrors !== undefined) {
      if (captionErrors[account.id] !== CaptionErrorTypes.NO_ERROR) {
        handleError();
      } else {
        setIsCountErrorVisible(false);
      }
    }
  }, [captionErrors]);

  const hasShortCaptionLimit = (accountType: SocialPlatform): boolean => {
    // Rather than this, can we not check if the captionCharacterLimits[accountType] <= 500?
    // 500 can be an exported constant from social-media-wizard.types.ts
    switch (accountType) {
      case SocialPlatform.THREADS:
      case SocialPlatform.TWITTER:
        return true;
      default:
        return false;
    }
  };

  const getCaptionPlaceholder = (): string => {
    switch (account.type) {
      case SocialPlatform.FACEBOOK:
      case SocialPlatform.FACEBOOK_PROFILE:
      case SocialPlatform.FACEBOOK_PAGE:
      case SocialPlatform.FACEBOOK_GROUP:
        return window.i18next.t('pmwjs_wizard_caption_placeholder_facebook');
      case SocialPlatform.TWITTER:
        return window.i18next.t('pmwjs_whats_happening');
      case SocialPlatform.LINKEDIN:
      case SocialPlatform.LINKEDIN_PAGE:
      case SocialPlatform.LINKEDIN_PROFILE:
        return window.i18next.t('pmwjs_what_to_talk_about');
      case SocialPlatform.TIKTOK:
        return window.i18next.t('pmwjs_describe_post_hashtags');
      case SocialPlatform.YOUTUBE:
        return window.i18next.t('pmwjs_add_a_caption_ellipses');
      case SocialPlatform.INSTAGRAM:
      case SocialPlatform.THREADS:
        return window.i18next.t('pmwjs_write_a_caption');
      case SocialPlatform.GOOGLE_BUSINESS_PROFILE:
        return window.i18next.t('pmwjs_post_update_for_x', {accountName: account.name});
      default:
        return '';
    }
  };

  const onAiCaptionClick = (viaOnboardingModal = false): void => {
    if (!postId) {
      return;
    }

    onAiCaptionOnboardingClick(viaOnboardingModal);
    setIsCaptionWriterOpen(true);
  };

  const onCaptionWriterClose = (): void => {
    setIsCaptionWriterOpen(false);
    closeAutoCaptionMode();
  };

  const onEmojiPickerOutsideClick = (): void => {
    setIsEmojiSelectorVisible(false);
  };

  const onEmojiSelected = (newCaption: string): void => {
    if (hasCommonCaption) {
      dispatch(updateCommonCaption(newCaption));
      dispatch(updatePublishingParamAllCaptions({caption: newCaption, isMultiCharacterChange: false}));
    } else {
      dispatch(
        updatePublishingParamCaption({
          idSocialMediaAccount: account.id,
          caption: newCaption,
          isMultiCharacterChange: false,
        })
      );
    }
  };

  useOutsideClick(emojiPickerRef, onEmojiPickerOutsideClick, iconRef.current, () => {
    return isEmojiSelectorVisible;
  });

  const triggerTextAreaAnimation = (): void => {
    if (textAreaContainerRef?.current) {
      textAreaContainerRef.current.classList.add(styles.animationFocus);

      setTimeout(() => {
        if (textAreaContainerRef?.current) {
          textAreaContainerRef.current.classList.remove(styles.animationFocus);
        }
      }, 700);
    }
  };

  const openUpsellDialogForAutoCaption = (): void => {
    void window.PMW.showPremiumOnlyFeatureDialog(window.PMW.PREMIUM_ONLY_FEATURE_NAME_AUTO_CAPTION).then(() => {
      window.PMW.gtm.trackGA4CustomEvent(GA4EventName.SOCIALWIZARD_AICAPTIONS_UPSELLING);
    });
  };

  const handleError = (): void => {
    if (captionErrors[account.id] === CaptionErrorTypes.COUNT_ERROR) {
      setIsHashtagErrorVisible(false);
      setIsCountErrorVisible(true);
    }

    if (captionErrors[account.id] === CaptionErrorTypes.HASHTAG_ERROR) {
      setIsHashtagErrorVisible(true);
      setIsCountErrorVisible(false);
    }
  };

  const onCaptionChange = (e: ChangeEvent<HTMLTextAreaElement>): void => {
    if (e.target) {
      const caption: string = e.target.value;

      if (hasCommonCaption) {
        dispatch(updateCommonCaption(caption));
        dispatch(
          updatePublishingParamAllCaptions({
            caption,
            isMultiCharacterChange: isCaptionChangeMultiCharacter,
          })
        );
      } else {
        dispatch(
          updatePublishingParamCaption({
            idSocialMediaAccount: account.id,
            caption,
            isMultiCharacterChange: isCaptionChangeMultiCharacter,
          })
        );
      }
    }

    if (isCaptionChangeMultiCharacter) {
      setIsCaptionChangeMultiCharacter(false);
    }
  };

  const onCaptionPaste = (): void => {
    setIsCaptionChangeMultiCharacter(true);
  };

  const onTextAreaClick = (): void => {
    captionTagsPopoverRef.current?.onTextAreaClick();
  };

  const autoCaptionsButton = (): ReactElement | null => {
    if (!isSocialMediaWizardAiCaptionsEnabled) {
      return null;
    }

    if (!isUserPremium()) {
      return getPremiumUpsellAiCaptionsButton();
    }

    return getDefaultAutoCaptionButtonWithIntroPopup();
  };

  const getCountError = (): ReactElement => {
    const twitterVerifiedCount = 10000;
    const characterLimit = account.isVerifiedTwitterUser ? twitterVerifiedCount : captionCharacterLimits[account.type];

    return (
      <div className="flex-row-align-center spacing-m-t-2">
        <Icon icon="icon-exclamation-triangle" size={IconSize.SIZE_ICON_16} className="spacing-m-r-2 content-danger" />
        <Text className="content-danger" size={TextSize.XSMALL} val={window.i18next.t('pmwjs_caption_long_error', {limit: characterLimit})} />
      </div>
    );
  };

  const getCharCounter = (): ReactElement | null => {
    if (!hasShortCaptionLimit(account.type)) {
      return null;
    }

    const characterLimit = captionCharacterLimits[account.type];

    return (
      <Text
        className={`${styles.charCounter} ${isCountErrorVisible ? styles.captionError : ''}`}
        size={TextSize.XXSMALL}
        type={TextType.BODY}
        val={`${captionCharCount} / ${characterLimit}`}
      />
    );
  };

  const getHashtagError = (): ReactElement => {
    return (
      <div className="flex-row-align-center spacing-m-t-2">
        <Icon icon="icon-exclamation-triangle" size={IconSize.SIZE_ICON_16} className="spacing-m-r-2 content-danger" />
        <Text className="content-danger" size={TextSize.XSMALL} val={window.i18next.t('pmwjs_hashtag_error')} />
      </div>
    );
  };

  const emojiPicker = useMemo((): ReactElement | null => {
    return (
      <div ref={emojiPickerRef} className={`${styles.emojiSelector} ${isEmojiSelectorVisible ? styles.visible : ''}`}>
        <EmojiPickerInput
          textAreaRef={textAreaRef}
          onEmojiSelectCallback={(newText) => {
            onEmojiSelected(newText);
          }}
        />
      </div>
    );
  }, [isEmojiSelectorVisible]);

  const getPremiumUpsellAiCaptionsButton = (): ReactElement => {
    return (
      <div className={styles.autoCaptionButtonContainer}>
        <AiButton text={window.i18next.t('pmwjs_ai_captions')} variant={AIButtonVariant.PREMIUM} size={Size.SMALL} onClick={openUpsellDialogForAutoCaption} />
      </div>
    );
  };

  const getAutoCaptionIntroPopup = (): ReactElement | null => {
    if (!shouldShowIntroPopup()) {
      return null;
    }

    return (
      <IntroPopup
        userHasSeenCallbackOnClose={afterCloseIntroPopup}
        title={window.i18next.t('pmwjs_say_goodbye_to_writers_block')}
        content={window.i18next.t('pmwjs_ai_caption_intro_text')}
        assetUrl={getAssetUrl('ai-captions-illustration.png')}
        leftOffset={70}
        shouldFlip={false}
        shouldShift
        primaryFooterAction={
          <Button
            text={window.i18next.t('pmwjs_try_now')}
            type={Type.PRIMARY}
            size={Size.XSMALL}
            customClasses={styles.secondaryGradeintColorBackground}
            onClick={() => {
              onAiCaptionClick(true);
            }}
          />
        }
      />
    );
  };

  const getAiCaptionReminderPopup = (): ReactElement | null => {
    if (isUserEligibleToSeeTagsPopOver) {
      return null;
    }

    if (!isVisibleAiCaptionReminderPopup) {
      return null;
    }

    return (
      <IntroPopup
        userHasSeenCallbackOnClose={onSeenReminderPopup}
        title={window.i18next.t('pmwjs_let_ai_do_the_writing_for_you')}
        content={window.i18next.t('pmwjs_ai_caption_reminder_popup_text')}
        assetUrl={getAssetUrl('ai-caption-reminder-graphic.png')}
        mediaWidth={320}
        leftOffset={70}
        hasSmoothFadeIn
        shouldShift
        shouldFlip={false}
      />
    );
  };

  const getDefaultAutoCaptionButtonWithIntroPopup = (): ReactElement => {
    return (
      <div className={styles.autoCaptionButtonContainer}>
        <AiButton
          buttonRef={aiCaptionButtonRef}
          text={window.i18next.t('pmwjs_help_me_write')}
          onClick={onAiCaptionClick as VoidFunction}
          variant={AIButtonVariant.SUBDUED}
          shape={AIButtonShape.ROUND}
          isPulsating={isAiCaptionButtonPulsating}
          size={Size.XSMALL}
          customClasses="spacing-p-t-2 spacing-p-b-2"
          icon="icon-ai-caption"
        />
        {getAutoCaptionIntroPopup()}
        {getAiCaptionReminderPopup()}
        {renderCaptionWriterPopover()}
      </div>
    );
  };

  const renderCaptionWriterPopover = (): ReactElement | null => {
    if (!isCaptionWriterOpen || !postId) {
      return null;
    }

    return (
      <CaptionWriterPopoverView
        isOpen={isCaptionWriterOpen}
        referenceElementRef={aiCaptionButtonRef}
        accountId={account.id}
        postId={postId}
        onClose={onCaptionWriterClose}
        onContentSelected={chooseCaption}
      />
    );
  };

  const renderTextAreaWithOverlay = (): ReactElement => {
    return (
      <HighlightedCaptionTextarea
        value={getCurrentCaption()}
        onChange={onCaptionChange}
        onPaste={onCaptionPaste}
        onClick={onTextAreaClick}
        placeholder={getCaptionPlaceholder()}
        accountType={account.type}
        isCountErrorVisible={isCountErrorVisible}
        textAreaRef={textAreaRef}
      />
    );
  };

  const renderSocialPostCaption = (): ReactElement => {
    return (
      <motion.div
        key="social-caption-animated-container"
        initial={{opacity: 0, height: AUTO_CAPTION_BOX_HEIGHT}}
        animate={{
          opacity: 1,
          height: CAPTION_TEXT_BOX_HEIGHT,
          transition: {
            opacity: {duration: 0.3},
            height: {duration: 0.3},
          },
        }}
        exit={{
          opacity: 0,
          height: AUTO_CAPTION_BOX_HEIGHT,
          transition: {
            opacity: {duration: 0.3},
            height: {duration: 1},
          },
        }}
        className={styles.captionContainer}
      >
        <div
          ref={textAreaContainerRef}
          className={`flex-v-row flex-content-space-between _full-height ${styles.textAreaContainer} ${isCountErrorVisible ? styles.captionError : ''}`}
          onFocus={startPulsatingButtonTimer}
          onBlur={abortPulsatingButtonTimer}
        >
          {renderTextAreaWithOverlay()}
          <div className={`flex-row-justify-between flex-align-items-center spacing-p-2 ${styles.textAreaContainerFooter}`}>
            {emojiPicker}
            <div className={styles.charCounterAndAIButtonContainer}>
              {getCharCounter()}
              {autoCaptionsButton()}
            </div>
          </div>
        </div>
        <CommonCaptionCheckbox accountIdsToPublishToCount={accountIdsToPublishTo.length} hasCommonCaption={hasCommonCaption} textAreaRef={textAreaRef} />
        {isCountErrorVisible ? getCountError() : null}
        {isHashtagErrorVisible ? getHashtagError() : null}
      </motion.div>
    );
  };

  const getEmojiFromLangString = (string?: string): string | undefined => {
    return string?.slice(0, 2);
  };

  const getTextFromLangString = (string?: string): string | undefined => {
    return string?.slice(2);
  };

  const renderAutoCaptionGeneration = (): ReactElement => {
    return (
      <div className={`${styles.title} ${styles.aiLoader}`}>
        <div>
          <span>{getEmojiFromLangString(autoGenerationLoadingCaption)}</span>
          <span className={styles.aiFontColor}>{getTextFromLangString(autoGenerationLoadingCaption)}</span>
        </div>
      </div>
    ) as ReactElement;
  };

  const getContent = (): ReactElement | null => {
    if (areCaptionsBeingAutoGenerated) {
      return renderAutoCaptionGeneration();
    }

    return renderSocialPostCaption();
  };

  return (
    <div
      ref={socialPostCaptionContainerRef}
      className={`${styles.socialCaptionContainer} social-caption-container`}
      style={{height: CAPTION_CONTAINER_TEXTBOX_HEIGHT}}
      key={`${account.id} ${platformSelection}`}
    >
      <AnimatePresence mode="popLayout" initial={false}>
        {getContent()}
      </AnimatePresence>

      <CaptionTagsPopover
        ref={captionTagsPopoverRef}
        textAreaContainerRef={textAreaContainerRef}
        onEligibilityDetermined={setIsUserEligibleToSeeTagsPopOver}
      />
    </div>
  );
}

export default React.memo(SocialPostCaption);
